"use client";
import React from "react";
import Header from "components/common/Header";
import Footer from "components/common/Footer";
import SectionDownloadApp from "components/common/SectionDownloadApp";
import Pagination from "components/common/Pagination";
import { BookingQRModal } from "components/common";
import OfferDetailModal from "components/home/OfferDetailModal";
import OfferCard from "components/home/Card/OfferCard";
import Layout from "components/home/Section/Layout";
import usePagination from "hooks/usePagination";
import { OFFER_PAGE_SIZE, fetchOfferPage } from "services/campaignPackage/server";
import { usePageContext } from "providers/PageProvider";
import { useT } from "providers/I18nProvider";
import { get } from "utils/lodash";

const chipCls = (on: boolean) =>
  `whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-semibold border transition-colors ${
    on
      ? "bg-primary text-white border-primary"
      : "bg-white text-black01 border-[#d8f3f2] hover:border-primary/60"
  }`;

/**
 * Trang /uu-dai — toàn bộ ưu đãi trải nghiệm (campaignPackage type=service) còn hiệu lực.
 * Filter theo danh mục chính + phân trang; click card mở modal chi tiết → QR tải app.
 * Lọc hiệu lực ở server bằng field thật (status/isDisplay/startDate/endDate) — isExpired/promotionStatus là virtual.
 */
const OfferListContainer: React.FC<{
  categories?: any[];
  initial?: { data: unknown[]; total: number } | null;
}> = ({ categories: categoriesProp, initial }) => {
  const t = useT();
  const { pageService } = usePageContext();
  // Ưu tiên danh mục truyền từ server; fallback pageService (localStorage) nếu là mảng hợp lệ
  const fromCtx = get(pageService, "categories");
  const categories: any[] =
    categoriesProp && categoriesProp.length
      ? categoriesProp
      : Array.isArray(fromCtx)
        ? fromCtx
        : [];
  const [category, setCategory] = React.useState<string>("");
  // Nhớ danh mục đang lọc: onLoadPage (chuyển trang) chỉ truyền page/limit
  const categoryRef = React.useRef<string>("");
  const [selected, setSelected] = React.useState<any>(null);
  const [qrOpen, setQrOpen] = React.useState(false);

  // Trang 1 (không lọc) đã render sẵn ở server → client chỉ gọi khi đổi trang/danh mục
  const pagination = usePagination({
    initialParams: {
      page: 1,
      limit: OFFER_PAGE_SIZE,
      initialItems: initial?.data,
      initialTotal: initial?.total,
    },
    loader: ({ category: cat, page, limit }: any) => {
      if (cat !== undefined) categoryRef.current = cat;
      return fetchOfferPage({ page, limit, category: categoryRef.current });
    },
  });

  React.useEffect(() => {
    if (!initial) pagination?.onReload?.();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onChangeCategory = (id: string) => {
    setCategory(id);
    pagination.submit({ category: id });
  };

  const items: any[] = pagination?.items || [];
  const total = pagination?.total || 0;

  return (
    <main className="relative flex flex-col min-h-screen">
      <Header />
      {/* sr-only: trang chưa có <h1> nào — Layout dưới dùng h2 (section dùng chung
          nhiều nơi, không đổi thành h1 vì trang chủ lặp lại component này nhiều lần). */}
      <h1 className="sr-only">{t("offers.title")}</h1>
      <div className="flex-1 bg-white">
        <Layout
          iconName="sun-fog"
          title={t("offers.title")}
          description={
            total > 0
              ? t("offers.descCount", { count: total })
              : t("offers.desc")
          }
          className="bg-gradient-to-b from-[#eefbfb] to-white"
        >
          {categories.length > 0 && (
            <div className="flex gap-2 overflow-x-auto scrollbar-hide px-6 pb-5">
              <button
                type="button"
                className={chipCls(!category)}
                onClick={() => onChangeCategory("")}
              >
                {t("home.allCategories")}
              </button>
              {categories.map((cat: any) => (
                <button
                  key={get(cat, "id")}
                  type="button"
                  className={chipCls(category === get(cat, "id"))}
                  onClick={() => onChangeCategory(get(cat, "id"))}
                >
                  {get(cat, "name")}
                </button>
              ))}
            </div>
          )}

          {!pagination?.isLoading && items.length === 0 ? (
            <div className="px-6 py-16 text-center text-gray-500">
              <p className="text-4xl mb-3">🫧</p>
              <p className="font-semibold text-black01 !mb-1">{t("offers.emptyTitle")}</p>
              <p className="text-sm !mb-0">{t("offers.emptyHint")}</p>
            </div>
          ) : (
            <div
              className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 px-4 md:px-6 transition-opacity ${
                pagination?.isLoading ? "opacity-60" : ""
              }`}
            >
              {items.map((pkg: any, i: number) => (
                <OfferCard
                  key={get(pkg, "id")}
                  pkg={pkg}
                  layout="grid"
                  priority={i < 2}
                  onClick={setSelected}
                />
              ))}
            </div>
          )}

          {total > OFFER_PAGE_SIZE && (
            <div className="flex justify-center mt-6">
              <Pagination pagination={pagination} />
            </div>
          )}
        </Layout>
        <SectionDownloadApp />
      </div>
      <Footer />

      <OfferDetailModal
        open={!!selected}
        pkg={selected}
        onClose={() => setSelected(null)}
        onBook={() => {
          setSelected(null);
          setQrOpen(true);
        }}
      />
      <BookingQRModal open={qrOpen} onClose={() => setQrOpen(false)} />
    </main>
  );
};

export default OfferListContainer;
